/**
 * Bitmap Helpers for Temporal Parking
 * Based on plans/ramp-temporal-parking.md
 *
 * Uses BigUint64Array for efficient bitwise operations on time slots.
 * Each bit represents a 15-minute bucket. 1 = occupied/reserved, 0 = free.
 */

import { ParkingRequest } from "./types";

// ==== Time discretization constants ====
export const SLOTS_PER_HOUR = 4; // 15-minute buckets per hour
export const SLOTS_PER_DAY = 96; // 15-minute buckets per day (24 * 4)
export const BITS_PER_WORD = 64; // 64-bit lane per element
const ONE = 1n;

// ==== Dynamic horizon support (v2+) ====
// Default to 24-hour window for backward compatibility
export const DEFAULT_HORIZON_HOURS = 24;

// Maximum recommended horizon: 90 days (2160 hours)
// - 8,640 slots (15-min buckets)
// - 135 BigUint64 words
// - ~1 KB memory per spot
// Larger horizons are technically supported but may impact performance
export const MAX_RECOMMENDED_HORIZON_HOURS = 2160; // 90 days

/**
 * Calculate total slots for a given horizon in hours
 * @param horizonHours - Planning horizon in hours (e.g., 12, 24, 48, 72, up to 2160 for 90 days)
 * @returns Total number of 15-minute buckets
 *
 * Examples:
 * - 24 hours = 96 slots
 * - 72 hours (3 days) = 288 slots
 * - 168 hours (7 days) = 672 slots
 * - 720 hours (30 days) = 2,880 slots
 * - 2160 hours (90 days) = 8,640 slots
 */
export function calculateTotalSlots(horizonHours: number): number {
  return horizonHours * SLOTS_PER_HOUR;
}

/**
 * Calculate number of 64-bit words needed for a given number of slots
 * @param totalSlots - Total number of 15-minute buckets
 * @returns Number of BigUint64 array elements needed
 */
export function calculateWordCount(totalSlots: number): number {
  return Math.ceil(totalSlots / BITS_PER_WORD);
}

// ==== Backward compatibility constants (v1) ====
// These maintain existing behavior for code that doesn't pass horizon explicitly
export const TOTAL_SLOTS = calculateTotalSlots(DEFAULT_HORIZON_HOURS);
export const WORD_COUNT = calculateWordCount(TOTAL_SLOTS);

/**
 * Create an empty bitmap mask
 * @param horizonHours - Optional planning horizon in hours (default: 24)
 * @returns Empty bitmap with appropriate size for the horizon
 */
export function makeEmptyMask(
  horizonHours: number = DEFAULT_HORIZON_HOURS
): BigUint64Array {
  const totalSlots = calculateTotalSlots(horizonHours);
  const wordCount = calculateWordCount(totalSlots);
  return new BigUint64Array(wordCount);
}

/**
 * Set a range of bits in a mask (inclusive on both ends)
 * @param mask - The mask to modify
 * @param start - Starting bucket index
 * @param end - Ending bucket index (inclusive)
 */
export function setRange(
  mask: BigUint64Array,
  start: number,
  end: number
): void {
  for (let i = start; i <= end; i++) {
    const wordIndex = Math.floor(i / BITS_PER_WORD);
    const bitIndex = BigInt(i % BITS_PER_WORD);
    mask[wordIndex] |= ONE << bitIndex;
  }
}

/**
 * Check if two masks intersect (have any overlapping bits)
 * @param a - First mask
 * @param b - Second mask
 * @returns true if any bits overlap
 */
export function intersects(a: BigUint64Array, b: BigUint64Array): boolean {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if ((a[i] & b[i]) !== 0n) {
      return true;
    }
  }
  return false;
}

/**
 * OR mask b into mask a (a |= b)
 * @param a - Target mask (modified in place)
 * @param b - Source mask
 */
export function orInto(a: BigUint64Array, b: BigUint64Array): void {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    a[i] |= b[i];
  }
}

/**
 * AND-NOT mask b from mask a (a &= ~b)
 * Removes all bits in b from a
 * @param a - Target mask (modified in place)
 * @param b - Source mask
 */
export function andNotInto(a: BigUint64Array, b: BigUint64Array): void {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    a[i] &= ~b[i];
  }
}

/**
 * Create a dwell mask for an aircraft's time on ground
 * Sets bits for [arrivalBucket, departureBucket) - note: departure is exclusive
 * @param aircraft - Aircraft with arrival and departure buckets
 * @param bufferMinutes - Optional buffer to add before arrival and after departure (default: 0)
 * @param horizonHours - Optional planning horizon in hours (default: 24)
 * @returns Bitmap mask for the dwell period (with buffers if specified)
 */
export function dwellMask(
  aircraft: ParkingRequest,
  bufferMinutes: number = 0,
  horizonHours: number = DEFAULT_HORIZON_HOURS
): BigUint64Array {
  const mask = makeEmptyMask(horizonHours);
  const totalSlots = calculateTotalSlots(horizonHours);

  // Note: departureBucket is exclusive (not set in the mask)
  // This follows convention where [arrival, departure) is the dwell interval

  // Convert buffer from minutes to buckets (15-minute buckets)
  const bufferBuckets = Math.ceil(bufferMinutes / 15);

  // Apply buffer: extend backwards for arrival, forwards for departure
  const start = Math.max(0, aircraft.arrivalBucket - bufferBuckets);
  const end = Math.min(
    totalSlots - 1,
    aircraft.departureBucket - 1 + bufferBuckets
  );

  if (end >= start) {
    setRange(mask, start, end);
  }
  return mask;
}

/**
 * Convert time string (HH:MM) to bucket index
 * @param timeStr - Time in "HH:MM" format (24-hour)
 * @param startTime - Planning window start time in "HH:MM" format (default: "00:00")
 * @returns Bucket index relative to start time
 *
 * Examples:
 * - timeStringToBucket("08:00", "00:00") → 32 (8 hours * 4 buckets/hour)
 * - timeStringToBucket("08:00", "06:00") → 8 (2 hours * 4 buckets/hour)
 * - timeStringToBucket("02:00", "18:00") → 32 (wraps to next day: 24-18+2 = 8 hours)
 */
export function timeStringToBucket(
  timeStr: string,
  startTime: string = "00:00"
): number {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const [startHours, startMinutes] = startTime.split(":").map(Number);

  const totalMinutes = hours * 60 + minutes;
  const startTotalMinutes = startHours * 60 + startMinutes;

  let deltaMinutes = totalMinutes - startTotalMinutes;

  // Handle wrap-around (e.g., night shift 18:00-06:00)
  // If time is before start time, assume it's the next day
  if (deltaMinutes < 0) {
    deltaMinutes += 24 * 60; // Add 24 hours
  }

  return Math.floor(deltaMinutes / 15);
}

/**
 * Convert bucket index to time string (HH:MM)
 * @param bucket - Bucket index (0-95 for 24h, higher for multi-day)
 * @param startTime - Planning window start time as Date object (default: midnight today)
 * @param showDay - Whether to show day prefix for multi-day scenarios (default: false)
 * @returns Time in "HH:MM" format or "Day N HH:MM" for multi-day
 *
 * Examples:
 * - bucketToTimeString(8, new Date("2024-11-01 06:00")) → "08:00" (8 buckets = 2 hours → 06:00 + 2h = 08:00)
 * - bucketToTimeString(32, new Date("2024-11-01 18:00")) → "02:00" (32 buckets = 8 hours → 18:00 + 8h = 02:00 next day)
 * - bucketToTimeString(140, new Date("2024-11-01 00:00"), true) → "Day 2 11:00" (140 buckets = 35 hours = 1d 11h)
 */
export function bucketToTimeString(
  bucket: number,
  startTime: Date = new Date(new Date().setHours(0, 0, 0, 0)),
  showDay: boolean = false
): string {
  const startHours = startTime.getHours();
  const startMinutes = startTime.getMinutes();
  const startTotalMinutes = startHours * 60 + startMinutes;

  const bucketMinutes = bucket * 15;
  const absoluteMinutes = startTotalMinutes + bucketMinutes;

  const days = Math.floor(absoluteMinutes / (24 * 60));
  const remainingMinutes = absoluteMinutes % (24 * 60);
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;

  const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;

  if (showDay && days > 0) {
    return `Day ${days + 1} ${timeStr}`;
  }

  return timeStr;
}

/**
 * Find available time windows in a spot's occupancy bitmap
 * Returns list of [start, end) bucket ranges where the spot is free
 *
 * @param occupancy - Spot's occupancy bitmap
 * @param requestStart - Desired start bucket (inclusive)
 * @param requestEnd - Desired end bucket (exclusive)
 * @returns Array of available windows as [startBucket, endBucket) pairs
 */
export function findAvailableWindows(
  occupancy: BigUint64Array,
  requestStart: number,
  requestEnd: number
): Array<[number, number]> {
  const windows: Array<[number, number]> = [];
  let windowStart: number | null = null;

  // Scan through requested range looking for free buckets
  for (let bucket = requestStart; bucket < requestEnd; bucket++) {
    const wordIndex = Math.floor(bucket / 64);
    const bitIndex = bucket % 64;
    const isOccupied = (occupancy[wordIndex] & (1n << BigInt(bitIndex))) !== 0n;

    if (!isOccupied) {
      // Free bucket - start or extend window
      if (windowStart === null) {
        windowStart = bucket;
      }
    } else {
      // Occupied bucket - close window if one was open
      if (windowStart !== null) {
        windows.push([windowStart, bucket]);
        windowStart = null;
      }
    }
  }

  // Close final window if we ended on a free bucket
  if (windowStart !== null) {
    windows.push([windowStart, requestEnd]);
  }

  return windows;
}
